import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, pageImageUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { toast } from "sonner";
import { MemberBadge, initials } from "@/components/MemberBadge";
import { Lock, X, ChevronLeft, ChevronRight, BookOpen, ZoomIn, ZoomOut, Maximize2, Trash2, MessageCircle } from "lucide-react";

function timeAgo(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function Paywall() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-24">
      <div data-testid="pages-paywall" className="max-w-xl border border-oxblood bg-oxblood/5 p-10">
        <div className="flex items-center gap-2 mb-3">
          <Lock strokeWidth={1.5} className="w-5 h-5 text-oxblood" />
          <span className="font-mono text-xs tracking-widest uppercase text-oxblood">Members only</span>
        </div>
        <h1 className="font-serif text-4xl text-ink tracking-tight mb-4">The Pages are sealed</h1>
        <p className="font-body text-text-muted leading-relaxed mb-6">
          The full gallery of scanned pages from the 1872 volume is reserved for members. Become a
          member to browse every leaf of the book.
        </p>
        <Link to="/membership" data-testid="pages-paywall-cta" className="inline-block bg-oxblood text-parchment px-8 py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors">
          Become a Member
        </Link>
      </div>
    </div>
  );
}

function Discussion({ pageId, user }) {
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/pages/${pageId}/comments`);
    setComments(data);
  }, [pageId]);

  useEffect(() => {
    setComments([]);
    load().catch(() => {});
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post(`/pages/${pageId}/comments`, { content });
      setContent("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    try {
      await api.delete(`/page-comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="page-discussion">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10 shrink-0">
        <MessageCircle strokeWidth={1.5} className="w-4 h-4 text-parchment/80" />
        <span className="font-mono text-xs tracking-widest uppercase text-parchment/80">
          Discussion · {comments.length}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5 min-h-0">
        {comments.length === 0 && (
          <p className="font-body italic text-parchment/50 text-sm">
            No remarks on this page yet. Start the conversation.
          </p>
        )}
        {comments.map((c) => (
          <div key={c.id} data-testid={`page-comment-${c.id}`} className="border-b border-white/10 pb-4 last:border-0">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 bg-oxblood text-parchment flex items-center justify-center font-mono text-[10px] shrink-0">
                {initials(c.author_name)}
              </div>
              <span className="font-body text-parchment text-sm">{c.author_name}</span>
              <MemberBadge role={c.author_role} isMember={c.author_is_member} />
              {(user?.id === c.author_id || user?.role === "admin") && (
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`page-comment-delete-${c.id}`}
                  className="ml-auto text-parchment/50 hover:text-parchment transition-colors"
                >
                  <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="font-body text-parchment/85 text-sm leading-relaxed whitespace-pre-wrap pl-9">
              {c.content}
            </p>
            <div className="font-mono text-[9px] tracking-widest uppercase text-parchment/40 mt-1 pl-9">
              {timeAgo(c.created_at)}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="p-4 border-t border-white/10 shrink-0">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          data-testid="page-comment-input"
          placeholder="Add a remark on this page…"
          className="w-full bg-white/5 border border-white/15 px-3 py-2 font-body text-sm text-parchment placeholder:text-parchment/40 resize-none focus:outline-none focus:ring-1 focus:ring-oxblood"
        />
        <button
          type="submit"
          disabled={busy || !content.trim()}
          data-testid="page-comment-submit"
          className="mt-2 w-full bg-oxblood text-parchment py-2.5 font-mono text-[10px] tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-50"
        >
          {busy ? "Posting…" : "Post Remark"}
        </button>
      </form>
    </div>
  );
}

export default function Pages() {
  const { user } = useAuth();
  const isMember = user?.has_membership || user?.role === "admin";
  const [pages, setPages] = useState(null);
  const [viewer, setViewer] = useState(-1);
  const scaleRef = useRef(1);
  const touchRef = useRef(null);

  useEffect(() => {
    if (!isMember) return;
    api.get("/pages").then(({ data }) => setPages(data)).catch(() => setPages([]));
  }, [isMember]);

  const move = useCallback(
    (dir) => {
      setViewer((v) => {
        if (v < 0 || !pages) return v;
        const n = v + dir;
        if (n < 0 || n >= pages.length) return v;
        return n;
      });
    },
    [pages]
  );

  useEffect(() => {
    if (viewer < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setViewer(-1);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer, move]);

  // Reset zoom tracking whenever the page changes
  useEffect(() => {
    scaleRef.current = 1;
  }, [viewer]);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) {
      touchRef.current = null;
      return;
    }
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    // Only turn pages when the image isn't zoomed in (otherwise a swipe pans it)
    if (!start || scaleRef.current > 1.05) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      move(dx < 0 ? 1 : -1);
    }
  };

  if (!isMember) return <Paywall />;

  const current = viewer >= 0 && pages ? pages[viewer] : null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-2xl mb-14 animate-fade-up">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4 flex items-center gap-2">
          <BookOpen strokeWidth={1.5} className="w-4 h-4" /> The UGRR Volume · 1872
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-none">The Pages</h1>
        <p className="font-body text-text-muted mt-6 leading-relaxed">
          Every scanned leaf of the original 1872 volume, in order. Select any page to open the
          reader — zoom into the detail and discuss it with fellow members.
        </p>
      </div>

      {pages === null ? (
        <div className="font-mono text-xs tracking-widest uppercase text-text-muted animate-pulse py-20">
          Retrieving pages…
        </div>
      ) : pages.length === 0 ? (
        <div data-testid="pages-empty" className="border border-dashed border-archive-border bg-surface p-16 text-center">
          <p className="font-serif text-3xl text-ink mb-3">No pages yet</p>
          <p className="font-body text-text-muted">The archivist has not yet added scanned pages to the volume.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {pages.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setViewer(i)}
              data-testid={`page-thumb-${p.page_number}`}
              className="group block bg-surface border border-archive-border offset-shadow text-left hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="aspect-[3/4] overflow-hidden bg-highlight relative">
                <img src={pageImageUrl(p.id)} alt={`Page ${p.page_number}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                <span className="absolute top-0 left-0 bg-ink/85 text-parchment font-mono text-[10px] tracking-widest uppercase px-2.5 py-1">
                  p. {p.page_number}
                </span>
              </div>
              {p.caption && <div className="p-3 font-body text-sm text-text-muted line-clamp-2">{p.caption}</div>}
            </button>
          ))}
        </div>
      )}

      {/* Reader with zoom + discussion */}
      {current && (
        <div className="fixed inset-0 z-50 bg-ink/95 flex flex-col" data-testid="page-viewer">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10 shrink-0 gap-3">
            <div className="font-mono text-[10px] sm:text-xs tracking-widest uppercase text-parchment/80 truncate">
              Page {current.page_number} · {viewer + 1} / {pages.length}
              {current.caption ? ` · ${current.caption}` : ""}
            </div>
            <button onClick={() => setViewer(-1)} data-testid="viewer-close" className="text-parchment/80 hover:text-parchment transition-colors shrink-0 p-1 -mr-1">
              <X strokeWidth={1.5} className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 flex flex-col lg:flex-row min-h-0">
            {/* Zoomable image */}
            <div
              className="relative flex-1 flex items-center min-h-0"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="viewer-swipe-area"
            >
              <button onClick={() => move(-1)} disabled={viewer === 0} data-testid="viewer-prev" className="absolute left-1 z-10 text-parchment/70 hover:text-parchment disabled:opacity-20 transition-colors p-2">
                <ChevronLeft strokeWidth={1.5} className="w-8 h-8" />
              </button>
              <TransformWrapper key={current.id} minScale={1} maxScale={6} doubleClick={{ mode: "toggle", step: 1.5 }} wheel={{ step: 0.15 }} onTransformed={(ref) => { scaleRef.current = ref.state.scale; }}>
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1 bg-ink/80 border border-white/10 p-1">
                        <button onClick={() => zoomOut()} data-testid="viewer-zoom-out" className="text-parchment/80 hover:text-parchment p-2 transition-colors">
                          <ZoomOut strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button onClick={() => zoomIn()} data-testid="viewer-zoom-in" className="text-parchment/80 hover:text-parchment p-2 transition-colors">
                          <ZoomIn strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button onClick={() => resetTransform()} data-testid="viewer-zoom-reset" className="text-parchment/80 hover:text-parchment p-2 transition-colors">
                          <Maximize2 strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="lg:hidden font-mono text-[9px] tracking-widest uppercase text-parchment/40">
                        Swipe to turn pages
                      </span>
                    </div>
                    <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
                      <img src={pageImageUrl(current.id)} alt={`Page ${current.page_number}`} className="max-h-[78vh] lg:max-h-[82vh] max-w-full object-contain select-none" draggable={false} />
                    </TransformComponent>
                  </>
                )}
              </TransformWrapper>
              <button onClick={() => move(1)} disabled={viewer === pages.length - 1} data-testid="viewer-next" className="absolute right-1 z-10 text-parchment/70 hover:text-parchment disabled:opacity-20 transition-colors p-2">
                <ChevronRight strokeWidth={1.5} className="w-8 h-8" />
              </button>
            </div>

            {/* Discussion sidebar */}
            <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-white/10 h-64 lg:h-auto shrink-0">
              <Discussion pageId={current.id} user={user} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
